import {
    BellRing,
    ChevronRight,
    CircleCheck,
    FileX,
    Hourglass,
    type LucideIcon,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { StatsResponse } from '~/api/types'
import { Card } from '~/components/ui/card'
import { Skeleton } from '~/components/ui/skeleton'
import { cn } from '~/lib/utils'

// 주의 신호 톤 → 좌측 점/아이콘 색·hover 강조.
type AttentionTone = 'danger' | 'warning' | 'info'
const ATTENTION_DOT: Record<AttentionTone, string> = {
    danger: 'bg-destructive',
    warning: 'bg-warning',
    info: 'bg-info',
}
const ATTENTION_ICON: Record<AttentionTone, string> = {
    danger: 'text-destructive',
    warning: 'text-warning',
    info: 'text-info',
}

interface AttentionSignal {
    key: string
    icon: LucideIcon
    label: string
    count: number
    hint: string
    tone: AttentionTone
    to: string
}

// 액션 가능한 주의 신호 카드 — 클릭하면 해당 화면으로 이동.
function AttentionCard({
    signal,
    onClick,
}: {
    signal: AttentionSignal
    onClick: () => void
}) {
    const Icon = signal.icon
    return (
        <button
            type='button'
            onClick={onClick}
            className='border-border bg-card focus-visible:ring-ring/40 hover:border-foreground/20 hover:bg-secondary/60 group flex items-center gap-3 rounded-[var(--radius)] border p-4 text-left shadow-xs transition-colors outline-none focus-visible:ring-2'>
            <span
                className={cn(
                    'inline-block size-1.5 shrink-0 rounded-full',
                    ATTENTION_DOT[signal.tone]
                )}
            />
            <Icon
                className={cn('size-5 shrink-0', ATTENTION_ICON[signal.tone])}
            />
            <div className='min-w-0 flex-1'>
                <div className='text-foreground text-[13px] font-medium'>
                    {signal.label}
                </div>
                <div className='text-muted-foreground text-[12px]'>
                    {signal.hint}
                </div>
            </div>
            <span className='text-foreground text-xl font-semibold tabular-nums'>
                {signal.count}
            </span>
            <ChevronRight className='text-muted-foreground size-4 shrink-0 transition-transform group-hover:translate-x-0.5' />
        </button>
    )
}

// 1) 주의 필요(Attention) — 가장 위. stats에서 액션 가능한 신호만 끌어온다.
export function AttentionPanel({
    loading,
    stats,
}: {
    loading: boolean
    stats: StatsResponse | null
}) {
    const navigate = useNavigate()

    const docFailed = stats?.documents.byStatus.failed ?? 0
    const certInProgress = stats
        ? stats.certification.collecting + stats.certification.scored
        : 0
    const alertsUnread = stats?.monitoring.alertsUnread ?? 0
    const signals: AttentionSignal[] = []
    if (alertsUnread > 0)
        signals.push({
            key: 'alerts',
            icon: BellRing,
            label: '미확인 모니터링 알림',
            count: alertsUnread,
            hint: `전체 ${stats?.monitoring.alertsTotal ?? 0}건 중`,
            tone: alertsUnread >= 5 ? 'danger' : 'warning',
            to: '/monitoring',
        })
    if (docFailed > 0)
        signals.push({
            key: 'docs',
            icon: FileX,
            label: '처리 실패 문서',
            count: docFailed,
            hint: '재업로드·점검 필요',
            tone: 'danger',
            to: '/content/knowledge',
        })
    if (certInProgress > 0)
        signals.push({
            key: 'cert',
            icon: Hourglass,
            label: '진행 중 인증평가',
            count: certInProgress,
            hint: '수집·채점 단계',
            tone: 'info',
            to: '/certification',
        })

    return (
        <section aria-label='주의 필요' className='mb-6'>
            {loading ? (
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} className='h-[68px]' />
                    ))}
                </div>
            ) : signals.length > 0 ? (
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                    {signals.map((s) => (
                        <AttentionCard
                            key={s.key}
                            signal={s}
                            onClick={() => navigate(s.to)}
                        />
                    ))}
                </div>
            ) : (
                <Card className='text-muted-foreground flex items-center gap-2.5 p-4 text-[13px]'>
                    <CircleCheck className='text-success size-5 shrink-0' />
                    특이사항 없음 — 미확인 알림·실패 문서·대기 작업이 없어요.
                </Card>
            )}
        </section>
    )
}
